import { useEffect, useState } from "react";

interface UserProfile {
  id: string;
  workos_user_id: string;
  email: string;
  role: string;
  status: "pending" | "approved" | "rejected" | "disabled";
}

interface HistoryItem {
  session_id?: string;
  timestamp: string;
  user_email?: string;
  grade_class: string;
  subject: string;
  quiz_type?: string;
  difficulty: string;
  score: number;
  total_questions: number;
}

interface Question {
  completed: boolean;
  question_number?: number;
  total_questions?: number;
  grade_class?: string;
  subject?: string;
  quiz_type?: string;
  difficulty?: string;
  question?: string;
  option_a?: string;
  option_b?: string;
  option_c?: string;
  option_d?: string;
  question_type?: string;
  image_key?: string;
  final_score?: number;
  total?: number;
}

interface SubmitResult {
  is_correct: boolean;
  correct_option: string;
  explanation: string;
  is_finished: boolean;
}

interface AdminUser {
  id: string;
  workos_user_id: string;
  email: string;
  name?: string;
  student_name?: string;
  grade_class?: string;
  school?: string;
  role: string;
  status: "pending" | "approved" | "rejected" | "disabled";
}

export const API_BASE_URL = (
  process.env.REACT_APP_API_URL ||
  ""
).replace(/\/$/, "");

const adminActionStyle = {
  padding: "3px 7px",
  fontSize: "0.7rem",
  lineHeight: 1.2,
  width: "auto",
  minWidth: "0",
  height: "28px",
  marginRight: "4px",
};

function apiUrl(path: string): string {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE_URL}${cleanPath}`;
}

function apiFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  return fetch(input, { ...init, credentials: "include" });
}

console.info("[AVYA] API backend:", API_BASE_URL);

function App() {
  const fetch = (input: RequestInfo | URL, init: RequestInit = {}) =>
    window.fetch(input, { ...init, credentials: "include" });

  const [currentUser, setCurrentUser] = useState<UserProfile | null>(() => {
    try {
      const saved = localStorage.getItem("avya_user");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const [screen, setScreen] = useState<
    "login" | "class" | "setup" | "quiz" | "results" | "admin"
  >(
    currentUser?.role === "admin"
      ? "admin"
      : currentUser
      ? "setup"
      : "login"
  );

  const [pendingAuth, setPendingAuth] = useState({
    email: "",
    name: "",
    workosUserId: "",
  });

  const [pendingProfile, setPendingProfile] = useState({
    kidsName: "",
    parentsName: "",
    dob: "",
    school: "",
  });

  const [gradeClass, setGradeClass] = useState("Class 5");
  const [subject, setSubject] = useState("math");
  const [quizType, setQuizType] = useState("Multiple Choice");
  const [difficulty, setDifficulty] = useState("Medium");

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [question, setQuestion] = useState<Question | null>(null);
  const [selectedOpt, setSelectedOpt] = useState<string | null>(null);
  const [isAnswerSubmitted, setIsAnswerSubmitted] = useState(false);
  const [explanation, setExplanation] = useState("");
  const [answerResult, setAnswerResult] = useState<SubmitResult | null>(null);

  const [qTimeRemaining, setQTimeRemaining] = useState(45);
  const [totalTimeRemaining, setTotalTimeRemaining] = useState(450);

  const [finalScore, setFinalScore] = useState(0);
  const [finalTotal, setFinalTotal] = useState(10);

  const [userHistory, setUserHistory] = useState<HistoryItem[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [adminHistory, setAdminHistory] = useState<HistoryItem[]>([]);
  const [llmUsage, setLlmUsage] = useState<any[]>([]);
  const [llmUsageMonth, setLlmUsageMonth] = useState("");
  const [adminView, setAdminView] = useState<"dashboard" | "attempt" | "report" | "llm-usage">("dashboard");
  const [userSort, setUserSort] = useState<{ key: "email" | "name" | "class" | "status"; direction: "asc" | "desc" }>({ key: "email", direction: "asc" });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [accessNotice, setAccessNotice] = useState<"rejected" | "disabled" | null>(null);

  function sortedAdminUsers() {
    return [...adminUsers].sort((left, right) => {
      const leftValue = userSort.key === "email"
        ? left.email
        : userSort.key === "status"
        ? left.status
        : userSort.key === "name"
        ? left.student_name || left.name || left.id
        : left.grade_class || "";
      const rightValue = userSort.key === "email"
        ? right.email
        : userSort.key === "status"
        ? right.status
        : userSort.key === "name"
        ? right.student_name || right.name || right.id
        : right.grade_class || "";
      const comparison = leftValue.localeCompare(rightValue);
      return userSort.direction === "asc" ? comparison : -comparison;
    });
  }

  function sortUsers(key: "email" | "name" | "class" | "status") {
    setUserSort((current) => ({ key, direction: current.key === key && current.direction === "asc" ? "desc" : "asc" }));
  }

  useEffect(() => {
    if (window.location.pathname !== "/auth/callback") return;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    if (!code || !state) return;

    void (async () => {
      try {
        const response = await apiFetch(apiUrl(`/api/auth/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`));
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || "WorkOS authentication failed.");

        if (data.needs_registration) {
          setPendingAuth({ email: data.email, name: "", workosUserId: data.workos_user_id || "" });
          setScreen("class");
        } else {
          loginUser(data.user);
        }
        window.history.replaceState({}, document.title, "/");
      } catch (error) {
        alert(error instanceof Error ? error.message : "WorkOS authentication failed.");
      }
    })();
  }, []);

  /*
   * ============================================================
   * LOAD ADMIN DATA
   * ============================================================
   *
   * This is the important fix.
   *
   * Previously the screen changed to "admin", but the data
   * was never loaded automatically.
   */
  async function loadAdminUsers() {
    if (!currentUser || currentUser.role !== "admin") {
      return;
    }

    try {
      console.info("[AVYA] Loading admin users...");

      const response = await fetch(
        apiUrl(
          `/api/admin/users?admin_email=${encodeURIComponent(
            currentUser.email
          )}`
        ),
        {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }
      );

      const data = await response.json();

      console.info("[AVYA] Admin users response:", response.status, data);

      if (!response.ok) {
        throw new Error(data.detail || "Failed to load users.");
      }

      setAdminUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("[AVYA] Admin users error:", error);

      setAdminUsers([]);

      alert(
        error instanceof Error
          ? error.message
          : "Failed to load user list."
      );
    }
  }

  async function loadAdminHistory() {
    if (!currentUser || currentUser.role !== "admin") {
      return;
    }

    try {
      const response = await fetch(
        apiUrl(
          `/api/dashboard/history?email=${encodeURIComponent(
            currentUser.email
          )}`
        ),
        {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail || "Failed to load admin history."
        );
      }

      setAdminHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("[AVYA] Admin history error:", error);
      setAdminHistory([]);
    }
  }

  async function loadLLMUsage() {
  if (!currentUser || currentUser.role !== "admin") {
    return;
  }

  try {
    const response = await fetch(
      apiUrl("/api/admin/llm-usage"),
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || "Failed to load LLM usage.");
    }

    setLlmUsageMonth(data.month || "");
    setLlmUsage(Array.isArray(data.usage) ? data.usage : []);
  } catch (error) {
    console.error("[AVYA] LLM usage error:", error);
    setLlmUsage([]);
  }
}

  /*
   * ============================================================
   * CURRENT USER / SCREEN MANAGEMENT
   * ============================================================
   */
  useEffect(() => {
    if (!currentUser) {
      setScreen("login");
      return;
    }

    setGradeClass("Class 5");

    if (currentUser.status !== "approved") {
      if (currentUser.status === "pending") {
        setMessage("Your registration request is pending approval.");
      } else if (currentUser.status === "disabled") {
        localStorage.removeItem("avya_user");
        setCurrentUser(null);
        setScreen("login");
        setAccessNotice("disabled");
      } else {
        setAccessNotice(currentUser.status);
      }
      return;
    }

    if (currentUser.role === "admin") {
      setScreen("admin");

      /*
       * IMPORTANT:
       * Automatically load pending users when admin logs in.
       */
      void loadAdminUsers();
      void loadAdminHistory();
    } else {
      setScreen("setup");
      void loadUserHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  /*
   * ============================================================
   * TOTAL QUIZ TIMER
   * ============================================================
   */
  useEffect(() => {
    if (!sessionId || screen !== "quiz") {
      return;
    }

    const interval = window.setInterval(() => {
      setTotalTimeRemaining((previous) => {
        if (previous <= 1) {
          window.clearInterval(interval);
          void handleTotalTimeExpired();
          return 0;
        }

        return previous - 1;
      });
    }, 1000);

    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, screen]);

  /*
   * ============================================================
   * QUESTION TIMER
   * ============================================================
   */
  useEffect(() => {
    if (
      !sessionId ||
      screen !== "quiz" ||
      isAnswerSubmitted
    ) {
      return;
    }

    const interval = window.setInterval(() => {
      setQTimeRemaining((previous) => {
        if (previous <= 1) {
          window.clearInterval(interval);
          void handleQuestionTimeExpired();
          return 0;
        }

        return previous - 1;
      });
    }, 1000);

    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sessionId,
    screen,
    isAnswerSubmitted,
    question?.question_number,
  ]);

  /*
   * ============================================================
   * REGISTRATION
   * ============================================================
   */
  async function completeRegistration(overrideClass?: string) {
    const selectedClass = overrideClass || gradeClass;

    if (
      !pendingProfile.kidsName ||
      !pendingProfile.parentsName ||
      !pendingProfile.dob ||
      !pendingProfile.school ||
      !selectedClass
    ) {
      alert(
        "Please fill in kid's name, parent's name, date of birth, school, and class."
      );
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        apiUrl("/api/auth/register"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            email: pendingAuth.email,
            workos_user_id: pendingAuth.workosUserId,
            kids_name: pendingProfile.kidsName,
            parents_name: pendingProfile.parentsName,
            dob: pendingProfile.dob,
            school: pendingProfile.school,
            grade_class: selectedClass,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail || "Registration failed."
        );
      }

      setMessage("Thanks for registration. User approval may take 2-3 days. Once approved, you will receive an email.");
      localStorage.removeItem("avya_user");
      setCurrentUser(null);
      setScreen("login");
      window.setTimeout(() => window.location.assign("/"), 3000);
    } catch (error) {
      console.error("[AVYA] Registration error:", error);

      alert(
        error instanceof Error
          ? error.message
          : "Registration failed."
      );
    } finally {
      setLoading(false);
    }
  }

  /*
   * ============================================================
   * LOGIN USER
   * ============================================================
   */
  function loginUser(user: UserProfile) {
  console.info("[AVYA] Login user:", user);

  setAccessNotice(null);
  setMessage("");

  if (user.status === "disabled") {
    setAccessNotice("disabled");
    return;
  }

  if (user.status === "pending") {
    setMessage("Your registration request is pending approval.");
    return;
  }

  if (user.status === "rejected") {
    setAccessNotice("rejected");
    return;
  }

  setCurrentUser(user);
  setGradeClass("Class 5");

  localStorage.setItem("avya_user", JSON.stringify(user));

  if (user.role === "admin") {
    setAdminView("dashboard");
    setScreen("admin");
  } else {
    setScreen("setup");
  }
}

  /*
   * ============================================================
   * LOGOUT
   * ============================================================
   */
  async function logout() {
    try {
      await fetch(apiUrl("/api/auth/logout"), { method: "POST", cache: "no-store" });
    } finally {
      localStorage.removeItem("avya_user");
    }
    setCurrentUser(null);
    setSessionId(null);
    setQuestion(null);
    setSelectedOpt(null);
    setIsAnswerSubmitted(false);

    setAdminUsers([]);
    setAdminHistory([]);
    setUserHistory([]);

    setPendingAuth({
      email: "",
      name: "",
      workosUserId: "",
    });

    setPendingProfile({
      kidsName: "",
      parentsName: "",
      dob: "",
      school: "",
    });

    setScreen("login");
  }

  /*
   * ============================================================
   * USER HISTORY
   * ============================================================
   */
  async function loadUserHistory() {
    if (!currentUser || currentUser.role === "admin") {
      return;
    }

    try {
      const response = await fetch(
        apiUrl(
          `/api/dashboard/history?email=${encodeURIComponent(
            currentUser.email
          )}`
        ),
        {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail || "Failed to load history."
        );
      }

      setUserHistory(
        Array.isArray(data) ? data : []
      );
    } catch (error) {
      console.error("[AVYA] History load failed:", error);
      setUserHistory([]);
    }
  }

  function showSetup() {
  if (!currentUser) {
    return;
  }

  // Clear all previous quiz state
  setSessionId(null);
  setQuestion(null);
  setSelectedOpt(null);
  setIsAnswerSubmitted(false);
  setExplanation("");
  setAnswerResult(null);
  setQTimeRemaining(0);
  setTotalTimeRemaining(0);
  setFinalScore(0);
  setFinalTotal(10);
  setMessage("");

  // Go back to quiz configuration
  if (currentUser.role === "admin") {
    setAdminView("attempt");
    setScreen("admin");
  } else {
    setScreen("setup");
  }

  // Refresh history
  void loadUserHistory();
}

  /*
   * ============================================================
   * START QUIZ
   * ============================================================
   */
  async function startQuiz() {
    if (!currentUser) {
      alert("Please login first.");
      return;
    }

    setLoading(true);
    setMessage("Generating Quiz...");

    try {
      const response = await fetch(
        apiUrl("/api/quiz/start"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            email: currentUser.email,
            subject,
            quiz_type: quizType,
            difficulty,
            grade_class: gradeClass,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail || "Quiz start failed."
        );
      }

      setSessionId(data.session_id);

      const perQuestion =
        difficulty === "Easy"
          ? 30
          : difficulty === "Hard"
          ? 60
          : 45;

      setQTimeRemaining(perQuestion);
      setTotalTimeRemaining(perQuestion * 10);
      setSelectedOpt(null);
      setIsAnswerSubmitted(false);
      setExplanation("");
      setAnswerResult(null);
      setScreen("quiz");

      await loadQuestion(data.session_id);
    } catch (error) {
      console.error("[AVYA] Quiz start error:", error);

      alert(
        error instanceof Error
          ? error.message
          : "Quiz generation failed."
      );
    } finally {
      setLoading(false);
      setMessage("");
    }
  }

  /*
   * ============================================================
   * LOAD QUESTION
   * ============================================================
   */
  async function loadQuestion(id?: string) {
    const activeSessionId = id || sessionId;

    if (!activeSessionId) {
      return;
    }

    try {
      const response = await fetch(
        apiUrl(
          `/api/quiz/question?session_id=${encodeURIComponent(
            activeSessionId
          )}`
        ),
        {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }
      );

      const data: Question = await response.json();

      if (!response.ok) {
        throw new Error(
          (data as any).detail ||
            "Failed to load question."
        );
      }

      if (data.completed) {
        setFinalScore(data.final_score || 0);
        setFinalTotal(data.total || 10);
        setScreen("results");
        return;
      }

      setQuestion(data);
      setSelectedOpt(null);
      setIsAnswerSubmitted(false);
      setExplanation("");
      setAnswerResult(null);

      const seconds =
        data.difficulty === "Easy"
          ? 30
          : data.difficulty === "Hard"
          ? 60
          : 45;

      setQTimeRemaining(seconds);
    } catch (error) {
      console.error("[AVYA] Question load error:", error);

      alert(
        error instanceof Error
          ? error.message
          : "Failed to load question."
      );
    }
  }

  function selectOption(option: string) {
    if (isAnswerSubmitted) {
      return;
    }

    setSelectedOpt(option);
  }

  async function submitOrNext() {
    if (!sessionId) {
      return;
    }

    if (!isAnswerSubmitted) {
      if (!selectedOpt) {
        alert("Please select an option.");
        return;
      }

      await submitAnswerInternal(selectedOpt);
    } else {
      await loadQuestion(sessionId);
    }
  }

  async function submitAnswerInternal(option: string) {
    if (!sessionId) {
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(
        apiUrl("/api/quiz/submit"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            session_id: sessionId,
            selected_option: option,
          }),
        }
      );

      const data: SubmitResult & { detail?: string } =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail || "Answer submission failed."
        );
      }

      setAnswerResult(data);
      setExplanation(data.explanation || "");
      setIsAnswerSubmitted(true);

      if (data.is_finished) {
        setMessage("Quiz completed.");
      }
    } catch (error) {
      console.error(
        "[AVYA] Submit answer error:",
        error
      );

      alert(
        error instanceof Error
          ? error.message
          : "Failed to submit answer."
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleQuestionTimeExpired() {
    if (isAnswerSubmitted) {
      return;
    }

    await submitAnswerInternal(
      selectedOpt || ""
    );
  }

  async function handleTotalTimeExpired() {
    if (!sessionId) {
      return;
    }

    try {
      const response = await fetch(
        apiUrl(
          `/api/quiz/question?session_id=${encodeURIComponent(
            sessionId
          )}`
        )
      );

      const data = await response.json();

      if (data.completed) {
        setFinalScore(data.final_score || 0);
        setFinalTotal(data.total || 10);
        setScreen("results");
      } else {
        await submitAnswerInternal(
          selectedOpt || ""
        );
      }
    } catch (error) {
      console.error(
        "[AVYA] Total timer error:",
        error
      );

      alert("Quiz time expired.");
    }
  }

  /*
   * ============================================================
   * ADMIN PANEL
   * ============================================================
   */
  async function showAdminPanel() {
    if (
      !currentUser ||
      currentUser.role !== "admin"
    ) {
      return;
    }

    setScreen("admin");

    await Promise.all([
      loadAdminUsers(),
      loadAdminHistory(),
    ]);
  }

  async function approveUser(
    targetEmail: string
  ) {
    if (
      !currentUser ||
      currentUser.role !== "admin"
    ) {
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(
        apiUrl(
          `/api/admin/approve?admin_email=${encodeURIComponent(
            currentUser.email
          )}`
        ),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            target_email: targetEmail,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail || "Approval failed."
        );
      }

      await loadAdminUsers();

      alert(
        data.message ||
          `${targetEmail} approved successfully.`
      );
    } catch (error) {
      console.error(
        "[AVYA] Approve error:",
        error
      );

      alert(
        error instanceof Error
          ? error.message
          : "Failed to approve user."
      );
    } finally {
      setLoading(false);
    }
  }

  async function revokeUser(
    targetEmail: string,
    action: "reject" | "disable" | "enable" | "delete" = "disable"
  ) {
    if (
      !currentUser ||
      currentUser.role !== "admin"
    ) {
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(
        apiUrl(
          `/api/admin/${action}?admin_email=${encodeURIComponent(
            currentUser.email
          )}`
        ),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            target_email: targetEmail,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail || `${action} failed.`
        );
      }

      await loadAdminUsers();

      alert(
        data.message ||
          `${targetEmail} ${action}d.`
      );
    } catch (error) {
      console.error(
        "[AVYA] Revoke error:",
        error
      );

      alert(
        error instanceof Error
          ? error.message
          : `Failed to ${action} user.`
      );
    } finally {
      setLoading(false);
    }
  }

  const optionClass = (
    option: string
  ) => {
    let className = "option-btn";

    if (
      selectedOpt === option &&
      !isAnswerSubmitted
    ) {
      className += " selected";
    }

    if (
      isAnswerSubmitted &&
      answerResult
    ) {
      if (
        answerResult.correct_option ===
        option
      ) {
        className += " correct";
      } else if (
        selectedOpt === option &&
        !answerResult.is_correct
      ) {
        className += " wrong";
      }
    }

    return className;
  };

  useEffect(() => {
    const preventContextMenu = (event: MouseEvent) => {
      event.preventDefault();
    };

    const preventKeyboardCapture = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();

      if (
        key === "printscreen" ||
        (event.ctrlKey && event.key === "p") ||
        (event.ctrlKey && event.shiftKey && ["i", "j", "c"].includes(key)) ||
        (event.ctrlKey && event.key === "u")
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const clearClipboard = () => {
      try {
        navigator.clipboard?.writeText("");
      } catch {
        // Ignore clipboard restrictions.
      }
    };

    document.addEventListener(
      "contextmenu",
      preventContextMenu
    );

    document.addEventListener(
      "keydown",
      preventKeyboardCapture,
      true
    );

    window.addEventListener(
      "keyup",
      (event) => {
        if (event.key === "PrintScreen") {
          clearClipboard();
        }
      },
      true
    );

    return () => {
      document.removeEventListener(
        "contextmenu",
        preventContextMenu
      );

      document.removeEventListener(
        "keydown",
        preventKeyboardCapture,
        true
      );
    };
  }, []);

  return (
    <>
      <header>
        <div className="brand">AVYA</div>

        {currentUser && (
          <div className="user-badge">
            <span>{currentUser.email}</span>

            {currentUser.role === "admin" && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
  setAdminView("report");
  setScreen("admin");
  void Promise.all([
    loadAdminUsers(),
    loadAdminHistory(),
  ]);
}}
              >
                Admin Panel
              </button>
            )}

            <button
              type="button"
              className="btn-secondary"
              onClick={logout}
            >
              Logout
            </button>
          </div>
        )}
      </header>

      {message && (
        <div
          style={{
            width: "100%",
            maxWidth: "800px",
            marginBottom: "15px",
            padding: "12px",
            borderRadius: "8px",
            background: "#e0e7ff",
            color: "#3730a3",
            textAlign: "center",
            fontWeight: 600,
          }}
        >
          {message}
        </div>
      )}

      {accessNotice && (
        <div className="card" role="alert">
          {accessNotice === "rejected"
            ? "Your registration request has been rejected. Please contact the administrator at "
            : "User is Disabled, kindly contact Administrator."}
          {accessNotice === "rejected" && (
            <a href="mailto:rkpmlops@gmail.com">rkpmlops@gmail.com</a>
          )}
          {accessNotice === "rejected" && "."}
        </div>
      )}

      {screen === "login" && (
        <main className="card">
          <h2>Welcome to Avya</h2>

          <p
            style={{
              color: "var(--text-muted)",
              marginBottom: "20px",
            }}
          >
            Sign in to continue.
          </p>


          <div
            style={{
              display: "grid",
              gap: "8px",
              marginTop: "15px",
            }}
          >
            {[
              {
                label: "Sign in with Google",
                provider: "GoogleOAuth",
                icon: (
                  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z"/>
                    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.9 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                    <path fill="#4CAF50" d="M24 44c5.4 0 10.3-1.8 14.1-5.1l-6.5-5.5C29.6 35 26.9 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.6 39.6 16.3 44 24 44z"/>
                    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.5 5.5C41.4 36 44 30.6 44 24c0-1.3-.1-2.7-.4-3.5z"/>
                  </svg>
                ),
              },
              {
                label: "Sign in with Microsoft",
                provider: "MicrosoftOAuth",
                icon: (
                  <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
                    <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
                    <rect x="12" y="1" width="10" height="10" fill="#7FBA00"/>
                    <rect x="1" y="12" width="10" height="10" fill="#00A4EF"/>
                    <rect x="12" y="12" width="10" height="10" fill="#FFB900"/>
                  </svg>
                ),
              },
              {
                label: "Sign in with Apple",
                provider: "AppleOAuth",
                icon: (
                  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fill="#000"
                      d="M16.365 1.43c0 1.14-.42 2.16-1.14 2.9-.83.83-2.15 1.47-3.24 1.38-.14-1.1.42-2.24 1.11-2.94.79-.83 2.15-1.44 3.27-1.34zm4.44 16.9c-.4.94-.6 1.35-1.11 2.19-.72 1.19-1.72 2.68-2.96 2.68-1.1 0-1.39-.72-2.9-.71-1.51.01-1.83.72-2.94.71-1.24-.01-2.19-1.35-2.91-2.54-1.99-3.29-2.2-7.15-.97-9.2.87-1.45 2.24-2.3 3.53-2.3 1.31 0 2.13.71 3.21.71 1.05 0 1.68-.71 3.21-.71 1.14 0 2.34.62 3.2 1.7-2.81 1.54-2.35 5.55.44 6.67z"
                    />
                  </svg>
                ),
              },
            ].map(({ label, provider, icon }) => (
              <button
                key={provider}
                type="button"
                onClick={() =>
                  (window.location.href = apiUrl(
                    `/api/auth/login?provider=${provider}`
                  ))
                }
                disabled={loading}
                style={{
                  width: "100%",
                  height: "42px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  padding: "8px 14px",
                  border: "1px solid var(--border)",
                  borderRadius: "7px",
                  background: "#fff",
                  color: "#222",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "background 0.15s, box-shadow 0.15s",
                }}
              >
                {icon}
                <span>{label}</span>
              </button>
            ))}
          </div>

        </main>
      )}

      {screen === "class" && (
        <main className="card">
          <h2>Complete Your Registration</h2>

          <p
            style={{
              color: "var(--text-muted)",
              marginBottom: "20px",
            }}
          >
            We couldn't find an existing account for {pendingAuth.email}. Please provide a few details to create one.
          </p>

          <div className="form-group">
            <label>Kid's Name</label>

            <input
              type="text"
              placeholder="Jane Doe"
              value={pendingProfile.kidsName}
              onChange={(event) =>
                setPendingProfile({
                  ...pendingProfile,
                  kidsName: event.target.value,
                })
              }
              required
            />
          </div>

          <div className="form-group">
            <label>Parent's Name</label>

            <input
              type="text"
              placeholder="John Doe"
              value={pendingProfile.parentsName}
              onChange={(event) =>
                setPendingProfile({
                  ...pendingProfile,
                  parentsName: event.target.value,
                })
              }
              required
            />
          </div>

          <div className="form-group">
            <label>Date of Birth</label>

            <input
              type="date"
              value={pendingProfile.dob}
              onChange={(event) =>
                setPendingProfile({
                  ...pendingProfile,
                  dob: event.target.value,
                })
              }
              required
            />
          </div>

          <div className="form-group">
            <label>School</label>

            <input
              type="text"
              placeholder="School name"
              value={pendingProfile.school}
              onChange={(event) =>
                setPendingProfile({
                  ...pendingProfile,
                  school: event.target.value,
                })
              }
              required
            />
          </div>

          <div className="form-group">
            <label>Assigned Class / Grade</label>

            <select
              value={gradeClass}
              onChange={(event) =>
                setGradeClass(event.target.value)
              }
            >
              {Array.from(
                { length: 10 },
                (_, index) => (
                  <option
                    key={index + 1}
                    value={`Class ${index + 1}`}
                  >
                    Class {index + 1}
                  </option>
                )
              )}
            </select>
          </div>

          <button
            type="button"
            className="btn"
            onClick={() =>
              void completeRegistration()
            }
            disabled={loading}
          >
            {loading
              ? "Registering..."
              : "Complete Registration"}
          </button>
        </main>
      )}

      {((screen === "setup" && currentUser) ||
        (screen === "admin" && currentUser?.role === "admin" && adminView === "attempt")) && currentUser && (
        <section className="card">
          <h2>Configure Your Quiz</h2>

          <div className="form-group">
            <label>Assigned Class</label>

            <select
              value={gradeClass}
              onChange={(event) => setGradeClass(event.target.value)}
              disabled={currentUser.role !== "admin"}
              style={{ fontWeight: 700, color: "var(--primary)" }}
            >
              {Array.from({ length: 10 }, (_, index) => <option key={index + 1}>Class {index + 1}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>Subject</label>

            <select
              value={subject}
              onChange={(event) =>
                setSubject(event.target.value)
              }
            >
              <option value="Math">
                Mathematics
              </option>

              <option value="Science">
                Science
              </option>

              <option value="Computer Science">
                Computer Science
              </option>

              <option value="English">
                English
              </option>

              <option value="Hindi">
                Hindi
              </option>

              <option value="General Knowledge">
                General Knowledge
              </option>

              <option value="Current Affairs">
                Current Affairs (India)
              </option>

              <option value="Social Studies">
                Social Studies
              </option>

              <option value="Multiple">
                Multiple / Mixed Topics
              </option>
            </select>
          </div>

          <div className="form-group">
            <label>Quiz Type</label>

            <select
              value={quizType}
              onChange={(event) =>
                setQuizType(event.target.value)
              }
            >
              <option value="Multiple Choice">
                Multiple Choice
              </option>

              <option value="Fill in the Blanks">
                Fill in the Blanks
              </option>

              <option value="True or False">
                True / False
              </option>
            </select>
          </div>

          <div className="form-group">
            <label>Difficulty</label>

            <select
              value={difficulty}
              onChange={(event) =>
                setDifficulty(event.target.value)
              }
            >
              <option value="Easy">
                Easy (30s / Question)
              </option>

              <option value="Medium">
                Medium (45s / Question)
              </option>

              <option value="Hard">
                Hard (60s / Question)
              </option>
            </select>
          </div>

          <button
            type="button"
            className="btn"
            onClick={() => void startQuiz()}
            disabled={loading}
          >
            {loading
              ? "Generating Quiz..."
              : "Generate Quiz"}
          </button>

          <div
            style={{
              marginTop: "30px",
              borderTop:
                "1px solid var(--border)",
              paddingTop: "20px",
            }}
          >
            <h3>
              Last 7 Days Performance History
            </h3>

            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Class</th>
                    <th>Subject</th>
                    <th>Difficulty</th>
                    <th>Score</th>
                  </tr>
                </thead>

                <tbody>
                  {userHistory.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          textAlign: "center",
                        }}
                      >
                        No recent quiz records found.
                      </td>
                    </tr>
                  ) : (
                    userHistory.map(
                      (item, index) => (
                        <tr key={index}>
                          <td>
                            {new Date(
                              item.timestamp
                            ).toLocaleDateString()}
                          </td>

                          <td>
                            {item.grade_class}
                          </td>

                          <td>
                            {item.subject}
                          </td>

                          <td>
                            {item.difficulty}
                          </td>

                          <td>
                            <b>
                              {item.score} /{" "}
                              {
                                item.total_questions
                              }
                            </b>
                          </td>
                        </tr>
                      )
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {screen === "quiz" && question && (
        <section className="card">
          <div className="quiz-params-badge">
            <span className="quiz-param-tag">
              Class: {question.grade_class}
            </span>

            <span className="quiz-param-tag">
              Subject: {question.subject}
            </span>

            <span className="quiz-param-tag">
              Difficulty: {question.difficulty}
            </span>
          </div>

          <div className="timer-container">
            <span>
              Question Time: {qTimeRemaining}s
            </span>

            <span>
              Total Time: {totalTimeRemaining}s
            </span>
          </div>

          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${
                  ((question.question_number ||
                    1) /
                    (question.total_questions ||
                      10)) *
                  100
                }%`,
              }}
            />
          </div>

          <p
            style={{
              color: "var(--text-muted)",
              fontSize: "0.85rem",
              marginBottom: "10px",
            }}
          >
            Question{" "}
            {question.question_number} of{" "}
            {question.total_questions}
          </p>

          <h3
            style={{
              marginBottom: "20px",
              fontWeight: 600,
            }}
          >
            {question.question}
          </h3>

          

          <div className="options-grid">
            {(
              ["A", "B", "C", "D"] as const
            ).map((option) => {
              const values = {
                A: question.option_a,
                B: question.option_b,
                C: question.option_c,
                D: question.option_d,
              };
              const value = values[option];

              return (
                <button
                  key={option}
                  type="button"
                  className={optionClass(
                    option
                  )}
                  disabled={
                    isAnswerSubmitted
                  }
                  onClick={() =>
                    selectOption(option)
                  }
                >
                  {option}){" "}
                  {String(value || "")}
                </button>
              );
            })}
          </div>

          {isAnswerSubmitted && (
            <div className="explanation-box">
              <strong>
                Explanation:
              </strong>{" "}
              {explanation}
            </div>
          )}

          <button
            type="button"
            className="btn"
            onClick={() =>
              void submitOrNext()
            }
            disabled={loading}
          >
            {loading
              ? "Submitting..."
              : isAnswerSubmitted
              ? answerResult?.is_finished
                ? "View Final Results"
                : "Next Question"
              : "Submit Answer"}
          </button>
        </section>
      )}

      {screen === "results" && (
        <section
          className="card"
          style={{ textAlign: "center" }}
        >
          <h2>Quiz Completed!</h2>

          <p
            style={{
              color: "var(--text-muted)",
              marginTop: "10px",
            }}
          >
            Your final score is:
          </p>

          <div
            style={{
              fontSize: "3.5rem",
              fontWeight: 800,
              color: "var(--primary)",
              margin: "20px 0",
            }}
          >
            {finalScore} / {finalTotal}
          </div>

          <div className="actions">
            <button
              type="button"
              className="btn"
              onClick={showSetup}
            >
              Take Another Quiz
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={logout}
              style={{ width: "100%" }}
            >
              Logout
            </button>
          </div>
        </section>
      )}

      {screen === "admin" &&
        currentUser &&
        currentUser.role === "admin" &&
        (
          <section className="card">
            <nav style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
              <button type="button" className="btn-secondary" onClick={() => setAdminView("dashboard")}>User Dashboard</button>
              <button type="button" className="btn-secondary" onClick={() => setAdminView("attempt")}>Attempt Quiz</button>
              <button type="button" className="btn-secondary" onClick={() => setAdminView("report")}>Quiz Report</button>
              <button
  type="button"
  className="btn-secondary"
  onClick={() => {
    setAdminView("llm-usage");
    void loadLLMUsage();
  }}
>
  LLM Usage
</button>
            </nav>
            <div
              style={{
                display: "flex",
                justifyContent:
                  "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <div>
                <h2>
                  User & Class Management
                </h2>

                <p
                  style={{
                    color:
                      "var(--text-muted)",
                  }}
                >
                  Review registered users and manage access.
                </p>
              </div>

              <button
                type="button"
                className="btn-secondary"
                onClick={() =>
                  void showAdminPanel()
                }
                disabled={loading}
              >
                {loading
                  ? "Loading..."
                  : "Refresh"}
              </button>
            </div>

            {adminView === "dashboard" && <>
            {/* ==================================================
                PENDING USERS
               ================================================== */}

            <div
              style={{
                marginBottom: "30px",
                padding: "15px",
                borderRadius: "10px",
                background: "#fff7ed",
                border:
                  "1px solid #fed7aa",
              }}
            >
              <h3
                style={{
                  marginBottom: "15px",
                }}
              >
                Pending Approval
              </h3>

              {adminUsers.filter(
                (user) =>
                  user.status === "pending" &&
                  user.role !== "admin"
              ).length === 0 ? (
                <p
                  style={{
                    color:
                      "var(--text-muted)",
                  }}
                >
                  No users are currently waiting for approval.
                </p>
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th><button type="button" onClick={() => sortUsers("name")}>Student Name</button></th>
                        <th><button type="button" onClick={() => sortUsers("email")}>Email</button></th>
                        <th><button type="button" onClick={() => sortUsers("class")}>Class</button></th>
                        <th><button type="button" onClick={() => sortUsers("status")}>Status</button></th>
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
  {adminUsers
    .filter(
      (user) =>
        user.status === "pending" &&
        user.role !== "admin"
    )
    .map((user) => (
      <tr key={user.email}>
        <td>{user.student_name || "-"}</td>
        <td>{user.email}</td>
        <td>{user.grade_class || "-"}</td>
        <td>
          <span
            style={{
              fontWeight: 700,
              color: "#d97706",
            }}
          >
            Pending
          </span>
        </td>
        <td>
          <button
            type="button"
            className="btn"
            style={adminActionStyle}
            onClick={() => void approveUser(user.email)}
            disabled={loading}
          >
            Approve
          </button>

          <button
            type="button"
            className="btn"
            style={adminActionStyle}
            onClick={() => void revokeUser(user.email, "reject")}
            disabled={loading}
          >
            Reject
          </button>
        </td>
      </tr>
    ))}
</tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ==================================================
                ALL USERS
               ================================================== */}

            <div>
              <h3
                style={{
                  marginBottom: "15px",
                }}
              >
                All Registered Users
              </h3>

              {adminUsers.length === 0 ? (
                <p
                  style={{
                    color:
                      "var(--text-muted)",
                  }}
                >
                  No registered users found.
                </p>
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
  <tr>
    <th>Student Name</th>
    <th>Class</th>
    <th>School</th>
    <th>Email</th>
    <th>Status</th>
    <th>Action</th>
  </tr>
</thead>

<tbody>
  {sortedAdminUsers().map((user) => (
    <tr key={user.email}>
      <td>{user.student_name || user.name || user.id}</td>

      <td>{user.grade_class || "-"}</td>

      <td>{user.school || "-"}</td>

      <td>{user.email}</td>

      <td>
        <span
          style={{
            fontWeight: 700,
            color:
              user.status === "approved"
                ? "#16a34a"
                : user.status === "pending"
                ? "#d97706"
                : "#dc2626",
          }}
        >
          {user.status}
        </span>
      </td>

      <td>
        {user.role === "admin" ? (
  <span
    style={{
      color: "var(--text-muted)",
      fontWeight: 600,
    }}
  >
    System Admin
  </span>
) : user.status === "pending" ? (
  <>
    <button
      type="button"
      className="btn"
      style={adminActionStyle}
      onClick={() => void approveUser(user.email)}
      disabled={loading}
    >
      Approve
    </button>

    <button
      type="button"
      className="btn-secondary"
      style={adminActionStyle}
      onClick={() => void revokeUser(user.email, "reject")}
      disabled={loading}
    >
      Reject
    </button>
  </>
) : user.status === "approved" ? (
  <button
    type="button"
    className="btn-secondary"
    style={adminActionStyle}
    onClick={() => void revokeUser(user.email, "reject")}
    disabled={loading}
  >
    Reject
  </button>
) : user.status === "rejected" ? (
  <>
    <button
      type="button"
      className="btn"
      style={adminActionStyle}
      onClick={() => void approveUser(user.email)}
      disabled={loading}
    >
      Approve
    </button>

    <button
      type="button"
      className="btn-secondary"
      style={adminActionStyle}
      onClick={() => void revokeUser(user.email, "disable")}
      disabled={loading}
    >
      Disable
    </button>
  </>
) : (
  <>
    <button
      type="button"
      className="btn"
      style={adminActionStyle}
      onClick={() => void revokeUser(user.email, "enable")}
      disabled={loading}
    >
      Enable
    </button>

    <button
      type="button"
      className="btn-danger"
      style={adminActionStyle}
      onClick={() => void revokeUser(user.email, "delete")}
      disabled={loading}
    >
      Delete
    </button>
  </>
)}
      </td>
    </tr>
  ))}
</tbody>
                  </table>
                </div>
              )}
            </div>

            </>}

            {adminView === "report" && <>
            {/* ==================================================
                ADMIN HISTORY
               ================================================== */}

            <div
              style={{
                marginTop: "35px",
                borderTop:
                  "1px solid var(--border)",
                paddingTop: "20px",
              }}
            >
              <h3>
                Last 7 Days Quiz History
              </h3>

              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Session</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>User</th>
                      <th>Class</th>
                      <th>Subject</th>
                      <th>Type</th>
                      <th>Difficulty</th>
                      <th>Score/10</th>
                    </tr>
                  </thead>

                  <tbody>
                    {adminHistory.length ===
                    0 ? (
                      <tr>
                        <td
                          colSpan={9}
                          style={{
                            textAlign:
                              "center",
                          }}
                        >
                          No recent quiz records found.
                        </td>
                      </tr>
                    ) : (
                      adminHistory.map(
                        (item, index) => (
                          <tr
                            key={index}
                          >
                            <td>{item.session_id || "-"}</td>
                            <td>
                              {new Date(
                                item.timestamp
                              ).toLocaleDateString()}
                            </td>

                            <td>
                              {new Date(item.timestamp).toLocaleTimeString()}
                            </td>

                            <td>
                              {item.user_email ||
                                "-"}
                            </td>

                            <td>
                              {
                                item.grade_class
                              }
                            </td>

                            <td>
                              {item.subject}
                            </td>

                            <td>{item.quiz_type || "Multiple Choice"}</td>

                            <td>{item.difficulty}</td>

                            <td>
                              <b>
                                {item.score} /{" "}
                                {
                                  item.total_questions
                                }
                              </b>
                            </td>
                          </tr>
                        )
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            </>}

            {adminView === "llm-usage" && <>
            <div
              style={{
                marginTop: "35px",
                borderTop: "1px solid var(--border)",
                paddingTop: "20px",
              }}
            >
              <h3>LLM Usage - {llmUsageMonth}</h3>

              {llmUsage.length === 0 ? (
                <p>No LLM usage recorded this month.</p>
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Provider</th>
                        <th>Model</th>
                        <th>Requests</th>
                        <th>Success</th>
                        <th>Failures</th>
                        <th>Input Tokens</th>
                        <th>Output Tokens</th>
                        <th>Thoughts/Reasoning Tokens</th>
                        <th>Total Tokens</th>
                      </tr>
                    </thead>
                    <tbody>
                      {llmUsage.map((usage, index) => (
                        <tr key={`${usage.provider}-${usage.model}-${index}`}>
                          <td>{usage.provider}</td>
                          <td>{usage.model}</td>
                          <td>{usage.usage_count}</td>
                          <td>{usage.success}</td>
                          <td>{usage.failures}</td>
                          <td>{usage.input_tokens}</td>
                          <td>{usage.output_tokens}</td>
                          <td>{usage.thoughts_tokens}</td>
                          <td>{usage.total_tokens}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            </>}
          </section>
        )}
    </>
  );
}

export default App;